// ============================================
// BidDataAgent 管理前端主应用
// 完整生产级实现 - 需要认证
// ============================================

import React, { useState, useEffect } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LogOut, User } from 'lucide-react'
import Dashboard from '@/components/Dashboard'
import TaskManager from '@/components/TaskManager'
import AgentMonitor from '@/components/AgentMonitor'
import DataQuality from '@/components/DataQuality'
import PlatformManager from '@/components/PlatformManager'
import LoginPage from './pages/login'

function App() {
  const [user, setUser] = useState<any>(null)
  const [health, setHealth] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // 检查是否在登录页面，如果是则跳过认证检查
    const currentPath = window.location.pathname;
    if (currentPath === '/login') {
      setLoading(false);
      return;
    }

    // 先检查认证，认证成功后再获取health
    checkAuth().then(() => {
      // 只在认证成功后才启动health检查
      fetchHealth()
      const interval = setInterval(fetchHealth, 30000)
      return () => clearInterval(interval)
    })
  }, [])

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/auth/session', {
        credentials: 'include'
      })

      if (!response.ok) {
        // API返回错误状态，重定向到登录页
        window.location.href = '/login'
        return
      }

      const result = await response.json()

      if (result.success && result.data && result.data.user) {
        setUser(result.data.user)
        setLoading(false) // 只在认证成功后设置loading为false
      } else {
        // 未认证，重定向到登录页
        window.location.href = '/login'
        return
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      window.location.href = '/login'
      return
    }
  }

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      })
      window.location.href = '/login'
    } catch (error) {
      console.error('Logout failed:', error)
      // 即使失败也重定向
      window.location.href = '/login'
    }
  }

  const fetchHealth = async () => {
    try {
      const response = await fetch('/api/health')
      const data = await response.json()
      setHealth(data)
    } catch (error) {
      console.error('Failed to fetch health:', error)
    }
  }

  // 检查当前路径，如果是登录页面则渲染登录组件
  const currentPath = window.location.pathname;
  if (currentPath === '/login') {
    return <LoginPage />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-white text-xl">加载中...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-900/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">
                BidDataAgent
                <span className="ml-2 text-sm font-normal text-slate-400">
                  金融AI招投标数据智能体
                </span>
              </h1>
            </div>
            <div className="flex items-center gap-4">
              {/* 用户信息 */}
              {user && (
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm">
                  <User className="h-4 w-4" />
                  <span>{user.email}</span>
                </div>
              )}

              {/* 系统状态 */}
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                health?.status === 'healthy'
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {health?.status === 'healthy' ? '系统正常' : '系统异常'}
              </div>

              {/* 刷新按钮 */}
              <Button
                variant="outline"
                size="sm"
                onClick={fetchHealth}
                className="border-slate-600 text-white hover:bg-slate-700"
              >
                刷新状态
              </Button>

              {/* 登出按钮 */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-red-600 text-red-400 hover:bg-red-900/20"
              >
                <LogOut className="h-4 w-4 mr-2" />
                登出
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="bg-slate-800/50 border border-slate-700">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-blue-600">
              📊 数据看板
            </TabsTrigger>
            <TabsTrigger value="tasks" className="data-[state=active]:bg-blue-600">
              📋 任务管理
            </TabsTrigger>
            <TabsTrigger value="agents" className="data-[state=active]:bg-blue-600">
              🤖 Agent监控
            </TabsTrigger>
            <TabsTrigger value="quality" className="data-[state=active]:bg-blue-600">
              🔧 数据质量
            </TabsTrigger>
            <TabsTrigger value="platforms" className="data-[state=active]:bg-blue-600">
              🌐 平台管理
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            <Dashboard health={health} />
          </TabsContent>

          <TabsContent value="tasks" className="space-y-6">
            <TaskManager />
          </TabsContent>

          <TabsContent value="agents" className="space-y-6">
            <AgentMonitor />
          </TabsContent>

          <TabsContent value="quality" className="space-y-6">
            <DataQuality />
          </TabsContent>

          <TabsContent value="platforms" className="space-y-6">
            <PlatformManager />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  )
}

export default App
